import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { RowMenu } from '../ui/RowMenu';
import { pointsLabel, pointsOf, PRESETS_KEY, PRESETS_RESOURCE, usePresets, type ChecklistPreset } from './checklists';
import { leaveGuarded, sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

function Problem({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="alert" role="alert">
      {error.status === 409 ? 'Ktoś już zmienił tę listę. Zamknij i otwórz ustawienia jeszcze raz.' : fieldIssues(error).map((i) => i.message).join(' ') || error.message}
    </p>
  );
}

function PresetForm({ base, onDone }: { base: ChecklistPreset | null; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(base?.name ?? '');
  const [text, setText] = useState(base?.items.join('\n') ?? '');
  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), items: pointsOf(text) };
      return base ? api(`/api/planning/checklist-presets/${base.id}`, { method: 'PATCH', body: { version: base.version, ...body } }) : api('/api/planning/checklist-presets', { method: 'POST', body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PRESETS_KEY });
      onDone();
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    save.mutate();
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const opening = useOpening({ name, text });
  const guardRef = useUnsavedGuard({
    dirty: !sameValues({ name, text }, opening),
    save: async () => {
      if (name.trim() === '') return false;
      await save.mutateAsync();
      return true;
    },
  });
  const points = pointsOf(text).length;
  return (
    <form ref={guardRef} className="settings-form" aria-label={base ? `Edycja listy: ${base.name}` : 'Nowa lista kontrolna'} onSubmit={submit}>
      <label className="field">
        <span>Nazwa listy</span>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" placeholder="np. Plener" />
      </label>
      <label className="field">
        <span>Punkty (jeden w wierszu)</span>
        <textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={'Agregat zatankowany\nNamiot FOH\nPlandeki na sprzęt'} />
        <span className="hint field-hint">{points > 0 ? pointsLabel(points) : 'Wpisz co najmniej jeden punkt.'}</span>
      </label>
      <Problem error={save.error} />
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending || name.trim() === '' || points === 0}>
          {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj listę'}
        </button>
        <button className="btn" type="button" onClick={onDone}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

/** Named checklists (owner remark 3/20): behind the gear of the checklist and in "Ustawienia firmy". */
export function ChecklistPresetsSettings({ me, heading = true }: { me: Me; heading?: boolean }) {
  const queryClient = useQueryClient();
  const list = usePresets();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  // "+ Dodaj" / "Edytuj" ask only about this list's own form, not about the screen under it (the wizard, when the
  // list is open in its dialog: the gear next to the field)
  const sectionRef = useRef<HTMLElement>(null);
  const switchTo = (next: string | 'new') => leaveGuarded(() => setEditing(next), sectionRef.current);
  const [confirm, setConfirm] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: (p: ChecklistPreset) => api(`/api/planning/checklist-presets/${p.id}?version=${p.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setConfirm(null);
      await queryClient.invalidateQueries({ queryKey: PRESETS_KEY });
    },
  });
  const canCreate = can(me, PRESETS_RESOURCE, 'create');
  const canEdit = can(me, PRESETS_RESOURCE, 'edit');
  const canDelete = can(me, PRESETS_RESOURCE, 'delete');
  const presets = list.data?.presets ?? [];
  return (
    <section ref={sectionRef} className="settings-section" aria-label="Listy kontrolne" data-feature-id="planning.checklist_presets">
      {heading && <h2>Listy kontrolne</h2>}
      <p className="hint">Gotowe listy punktów do sprawdzenia, np. „Plener” albo „Hala”. Dodasz je do wydarzenia w kreatorze i w zakładce „Lista kontrolna”; punkty się nie dublują.</p>
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać list kontrolnych'}</p>}
      {list.data && presets.length === 0 && <p className="muted">Nie ma jeszcze żadnej listy.</p>}
      <ul className="settings-list" aria-label="Gotowe listy kontrolne">
        {presets.map((p) =>
          editing === p.id ? (
            <li key={p.id}>
              <PresetForm base={p} onDone={() => setEditing(null)} />
            </li>
          ) : (
            <li key={p.id} className="settings-row">
              <div className="settings-row-main">
                <strong>{p.name}</strong>
                <span className="muted">
                  {pointsLabel(p.items.length)}: {p.items.slice(0, 3).join(', ')}
                  {p.items.length > 3 ? ', …' : ''}
                </span>
              </div>
              {confirm === p.id ? (
                <span className="settings-confirm" role="group" aria-label={`Usunąć listę ${p.name}?`}>
                  <span>Usunąć?</span>
                  <button className="btn small danger" type="button" disabled={remove.isPending} onClick={() => remove.mutate(p)}>
                    Usuń
                  </button>
                  <button className="btn small ghost" type="button" onClick={() => setConfirm(null)}>
                    Nie
                  </button>
                </span>
              ) : (
                <RowMenu
                  label={`Działania: ${p.name}`}
                  actions={[...(canEdit ? [{ label: 'Edytuj', onSelect: () => switchTo(p.id) }] : []), ...(canDelete ? [{ label: 'Usuń', danger: true, onSelect: () => setConfirm(p.id) }] : [])]}
                />
              )}
            </li>
          ),
        )}
      </ul>
      <Problem error={remove.error} />
      {canCreate && editing === 'new' && <PresetForm base={null} onDone={() => setEditing(null)} />}
      {canCreate && editing !== 'new' && (
        <button className="btn small" type="button" onClick={() => switchTo('new')}>
          + Dodaj listę
        </button>
      )}
    </section>
  );
}
